import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Spin,
  Typography,
  Button,
  Space,
  Form,
  DatePicker,
  Select,
  message, Row, Col, Input
} from 'antd';
import PersistentDrawerLeft from '../../reusable/admin/Main';
import { restApi } from '../../../constants/api';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title } = Typography;

const Wrapper = styled.div`
  padding: 24px;
`;

const StyledCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const StyledTitle = styled(Title)`
  && {
    margin: 0;
    font-weight: 600;
    font-size: 26px;
  }
`;

const SpinnerContainer = styled.div`
  text-align: center;
  padding: 3rem;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  .ant-table-thead > tr > th {
    background-color: #77d6c9 !important;
    color: black;
  }
`;

const AddButton = styled(Button)`
  background-color: #52c41a !important;
  border-color: #52c41a !important;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.4);
`;

const ActionButton = styled(Button)`
  border-radius: 6px;
  background-color: #5a7a76;
  border: none;
  color: white;
  display: flex;
  align-items: center;
`;
const AttendanceButton = styled(Button)`
  border-radius: 6px;
  background-color: #383838;
  border: none;
  color: white;
  display: flex;
  align-items: center;
`;
const ViewAttendanceButton = styled(Button)`
  border-radius: 6px;
  background-color: #1f1f43;
  border: none;
  color: white;
  display: flex;
  align-items: center;
`;
const MarkAttendanceButton = styled(Button)`
  border-radius: 6px;
  background-color: #87872d;
  border: none;
  color: white;
  display: flex;
  align-items: center;
`;

const StyledButton = styled(Button)`
  background-color: #1890ff;
  color: white;
  border-radius: 6px;
`;

// ✅ Attendance Form Row Component
const AttendanceFormRow: React.FC<{
  record: any;
  onFail: any;
  onSuccess: () => void;
}> = ({ record, onSuccess, onFail }) => {
  const [form] = Form.useForm();
  const [loadingForm, setLoadingForm] = useState(false);

  const handleManualSubmit = async (values: any) => {
    const payload = {
      user_id: record.user_id,
      attendance_date: dayjs(values.attendance_date).format('YYYY-MM-DD'),
      status: values.status || 'PRESENT',
      cycle_id: record.attendance_cycle_id,
    };

    try {
      setLoadingForm(true);
      await restApi('/api/attendance', 'POST', payload);
      message.success('Attendance marked successfully');
      form.resetFields();
      onSuccess(); // Update parent state
    } catch (err: any) {
      const e = err?.response?.data?.message || 'Failed to mark attendance';
      message.error(e);
      onFail();
    } finally {
      setLoadingForm(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleManualSubmit}
      initialValues={{ status: 'PRESENT', attendance_date: dayjs(), }}
      style={{ padding: 16, background: '#fafafa' }}
    >
      <Row gutter={16} align="bottom">
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            label="Attendance Date"
            name="attendance_date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status">
              <Select.Option value="PRESENT">PRESENT</Select.Option>
              <Select.Option value="ABSENT">ABSENT</Select.Option>
              <Select.Option value="LEAVE">LEAVE</Select.Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={3}>
          <Form.Item label=" ">
            <StyledButton
              type="primary"
              htmlType="submit"
              loading={loadingForm}
              disabled={loadingForm}
              style={{ width: '100%' }}
            >
              {loadingForm ? 'Uploading...' : 'Submit Attendance'}
            </StyledButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>

  );
};

const AttendanceCycleList = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState<any>([]);

  // ✅ Only added filters
  const [searchUser, setSearchUser] = useState('');
  const [searchDepartment, setSearchDepartment] = useState('');

  const rawRole = localStorage.getItem('role');
  const role = rawRole ? rawRole.trim().toUpperCase() : null;

  useEffect(() => {
    let mounted = true;
    let requestId = 0;
    const fetchData = async () => {
      const currentRequest = ++requestId;
      try {
        const response: any = await restApi('/api/users/cycles', 'GET');
        if (mounted && currentRequest === requestId) setData(response?.data || []);
      } catch (error) {
        console.error('Error fetching attendance cycles:', error);
      } finally {
        if (mounted && currentRequest === requestId) setLoading(false);
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'usersUpdatedAt') fetchData();
    };
    fetchData();
    window.addEventListener('focus', fetchData);
    window.addEventListener('users-updated', fetchData);
    window.addEventListener('storage', onStorage);
    return () => {
      mounted = false;
      window.removeEventListener('focus', fetchData);
      window.removeEventListener('users-updated', fetchData);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD MMM YYYY').toUpperCase();
  };

  // ✅ filtered data (client-side only; API/logic unchanged)
  const filteredData = useMemo(() => {
    const uq = searchUser.trim().toLowerCase();
    const dq = searchDepartment.trim().toLowerCase();

    if (!uq && !dq) return data;

    return (data || []).filter((r: any) => {
      const fullName = `${r?.prefixName ?? ''}. ${r?.user_name ?? ''}`.toLowerCase();
      const userNameOnly = `${r?.user_name ?? ''}`.toLowerCase();
      const deptName = `${r?.department_name ?? ''}`.toLowerCase();

      const userOk = !uq || fullName.includes(uq) || userNameOnly.includes(uq);
      const deptOk = !dq || deptName.includes(dq);

      return userOk && deptOk;
    });
  }, [data, searchUser, searchDepartment]);

  const columns: any = [
    {
      title: 'Cycle ID',
      dataIndex: 'attendance_cycle_id',
      key: 'attendance_cycle_id',
      align: 'center',
    },
    {
      title: 'User Name',
      dataIndex: 'user_name',
      key: 'user_name',
      render: (_: string, record: any) => (
        <>
          {record.prefixName}. {record.user_name}
        </>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: 'Designation',
      dataIndex: 'user_type_name',
      key: 'user_type_name',
    },
    {
      title: 'Total Days To Cover',
      dataIndex: 'total_number_of_days_need_to_cover',
      key: 'total_number_of_days_need_to_cover',
      align: 'center',
    },
    {
      title: 'Reminder Days',
      dataIndex: 'no_of_days_reminders',
      key: 'no_of_days_reminders',
      align: 'center',
    },
    {
      title: 'Days Covered',
      dataIndex: 'total_number_of_days_covered',
      key: 'total_number_of_days_covered',
      align: 'center',
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (text: string) => formatDate(text),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (text: string) => formatDate(text),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_: any, record: any) => (
        <Space size="small">
          {role !== 'VIEWER' && (
            <Link to={`/admin/users/updateattendancecycle/${record.attendance_cycle_id}`}>
              <ActionButton size="small" icon={<EditOutlined />}>Edit</ActionButton>
            </Link>
          )}
          {role !== 'VIEWER' && (
            <Link to={`/admin/users/cycles/${record.attendance_cycle_id}/attendance`}>
              <AttendanceButton size="small" icon={<EditOutlined />}>Attendance</AttendanceButton>
            </Link>
          )}
          <Link to={`/admin/users/cycles/${record.attendance_cycle_id}/attendance/view`}>
            <ViewAttendanceButton size="small" icon={<EyeOutlined />}>View</ViewAttendanceButton>
          </Link>
          {role !== 'VIEWER' && (
            <MarkAttendanceButton
              size="small"
              icon={< CalendarOutlined />}
              onClick={() => {
                setExpandedRowKeys((prev: any) =>
                  prev.includes(record.attendance_cycle_id)
                    ? prev.filter((key: any) => key !== record.attendance_cycle_id)
                    : [...prev, record.attendance_cycle_id]
                );
              }}
            >
              {expandedRowKeys.includes(record.attendance_cycle_id)
                ? 'Hide Form'
                : 'Mark Attendance'}
            </MarkAttendanceButton>
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record: any) => (
    <AttendanceFormRow
      record={record}
      onFail={() => {
        setExpandedRowKeys((prev: any[]) =>
          prev.filter((key) => key !== record.attendance_cycle_id)
        );
      }}
      onSuccess={() => {
        setData((prevData: any) =>
          prevData.map((item: any) =>
            item.attendance_cycle_id === record.attendance_cycle_id
              ? {
                ...item,
                total_number_of_days_covered:
                  item.total_number_of_days_covered + 1,
              }
              : item
          )
        );

        // ✅ Collapse the expanded row
        setExpandedRowKeys((prev: any[]) =>
          prev.filter((key) => key !== record.attendance_cycle_id)
        );
      }}
    />
  );

  return (
    <PersistentDrawerLeft>
      <Wrapper>
        <StyledCard>
          <Header>
            <StyledTitle level={3}>📅 Attendance Cycle List</StyledTitle>
            {role !== 'VIEWER' && (
              <Link to="/admin/users/cycles/create">
                <AddButton type="primary" icon={<PlusOutlined />} size="middle">
                  Create New Cycle
                </AddButton>
              </Link>
            )}
          </Header>

          {/* ✅ ONLY added filters (no extra wrapper styling) */}
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} md={6}>
              <Input
                allowClear
                placeholder="Search User"
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                allowClear
                placeholder="Search Department"
                value={searchDepartment}
                onChange={(e) => setSearchDepartment(e.target.value)}
              />
            </Col>
          </Row>

          {loading ? (
            <SpinnerContainer>
              <Spin size="large" />
            </SpinnerContainer>
          ) : (
            <TableWrapper>
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="attendance_cycle_id"
                bordered
                expandable={{
                  expandedRowRender,
                  expandedRowKeys,
                  onExpandedRowsChange: setExpandedRowKeys,
                  rowExpandable: () => true,
                }}
                pagination={{ pageSize: 10 }}
                size="middle"
              />
            </TableWrapper>
          )}
        </StyledCard>
      </Wrapper>
    </PersistentDrawerLeft>
  );
};

export default AttendanceCycleList;
